'use client'

/**
 * SortableTable — the interactive, click-to-sort table used across the /data
 * surfaces (owner directive 07/20, Section E item 14: "sortable tables").
 *
 * Brand-standard treatment identical to the static series/energy tables (white
 * card, gray-50 uppercase header, hairline dividers, right-aligned tabular
 * numerals, overflow scroll container), plus:
 *  • Every column header is a button; clicking sorts by that column and toggles
 *    asc/desc, with an arrow indicator. A default sort column/direction is set
 *    on mount so the first paint matches the server order.
 *  • Rows are keyed and can be muted (below-threshold) exactly as before.
 *  • FROZEN HEADER ROW AND FROZEN FIRST COLUMN (FRIDGE-363, owner directive):
 *    the whole top row stays put while the rows scroll under it, and the first
 *    column stays put while the numeric columns scroll sideways. On the league
 *    tables that matters — /data/brand-index is 76 rows and the price-index
 *    series is 468, and a figure is unreadable once its column heading and its
 *    brand name have both scrolled away.
 *
 * Three CSS constraints make that work, and each one is load-bearing:
 *  1. `position: sticky` resolves against the nearest scrollable ancestor, not
 *     the viewport. Tailwind's `overflow-x-auto` leaves `overflow-y: visible`,
 *     which the spec then computes to `auto` — so this container was ALREADY a
 *     vertical scrollport, just one with no height limit, and a `top-0` header
 *     would have pinned to a box that scrolls off the screen with the page.
 *     The container therefore takes an explicit `max-height`: the table scrolls
 *     inside its own card, which is what makes "stays as you scroll" true.
 *  2. `border-collapse: collapse` drops the borders of sticky cells, so the
 *     table is `border-separate` with zero spacing and every divider moved off
 *     `<tr>` onto the cells themselves.
 *  3. A sticky cell needs its own background or the scrolling content shows
 *     through it. The body's first column uses `bg-inherit` so it picks up the
 *     row's background — including the hover tint — instead of double-painting
 *     a translucent one over itself.
 *
 * Values arrive already-derived and already-formatted from the page (which owns
 * the single JSON importer), so sorting never recomputes or invents anything —
 * it only reorders rows by a numeric/string sort key the page supplies.
 */
import { useMemo, useState, type ReactNode } from 'react'
import { ArrowUp, ArrowDown } from 'lucide-react'
import { TableScrollHint } from '@/components/ui/table-scroll-hint'

export interface SortableColumn<Row> {
  key: string
  header: string
  /** true → right-aligned numeric column. */
  numeric?: boolean
  /** Sort key for this column; numbers sort numerically, strings lexically. */
  sortValue: (row: Row) => number | string
  /** Rendered cell content. */
  render: (row: Row) => ReactNode
}

interface SortableTableProps<Row> {
  columns: SortableColumn<Row>[]
  rows: Row[]
  rowKey: (row: Row) => string
  /** Optional per-row muting (below-threshold rows). */
  muted?: (row: Row) => boolean
  /** Column key to sort by initially. Defaults to the first column. */
  defaultSortKey?: string
  defaultDir?: 'asc' | 'desc'
  minWidth?: number
  caption?: string
  /**
   * Height cap on the scroll container, as a CSS length. The frozen header only
   * means anything while the table scrolls inside this box (see note 1 above),
   * so this is what "sticky" is measured against. Default suits the long league
   * tables; a short table simply never reaches the cap.
   */
  maxHeight?: string
  /** Set false for a table whose first column is not its row label. */
  stickyFirstColumn?: boolean
}

// Frozen header row. The background is required (see note 3); the bottom
// hairline replaces the divider `border-collapse` would have drawn.
const TH_BASE =
  'px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-gray-500 whitespace-nowrap select-none' +
  ' sticky top-0 bg-gray-50 border-b border-gray-200'
const TH = TH_BASE + ' z-20'
const TD = 'px-4 py-2.5 whitespace-nowrap border-t border-gray-100'
const TD_NUM = 'px-4 py-2.5 whitespace-nowrap text-right tabular-nums border-t border-gray-100'
/** Frozen first column: body cells sit under the header, above the other cells. */
const STICKY_CELL = ' sticky left-0 z-10 bg-inherit border-r border-gray-200'
/**
 * The header's own first cell is frozen on BOTH axes, so it must outrank the
 * rest of the header row as well as the body's frozen column. Built from
 * TH_BASE rather than TH so exactly one `z-*` is present: two would leave the
 * winner to Tailwind's stylesheet order rather than to this file.
 */
const TH_STICKY_FIRST = TH_BASE + ' z-30 left-0 border-r border-gray-200'
/**
 * The row tint, as a SOLID colour rather than the translucent `#0469f0`/3% the
 * table used before. The frozen cell inherits this background, and a
 * translucent value would be painted twice — once by the row, once by the cell
 * — leaving the first column visibly darker than the rest of its own row.
 * rgb(248,251,255) is `#0469f0` at 3% composited over white.
 */
const ROW = 'bg-white hover:bg-[#f8fbff] transition-colors'

export function SortableTable<Row>({
  columns,
  rows,
  rowKey,
  muted,
  defaultSortKey,
  defaultDir = 'desc',
  minWidth = 640,
  caption,
  maxHeight = '70vh',
  stickyFirstColumn = true,
}: SortableTableProps<Row>) {
  const [sortKey, setSortKey] = useState(defaultSortKey ?? columns[0]?.key)
  const [dir, setDir] = useState<'asc' | 'desc'>(defaultDir)

  const sorted = useMemo(() => {
    const col = columns.find((c) => c.key === sortKey)
    if (!col) return rows
    const copy = [...rows]
    copy.sort((a, b) => {
      const av = col.sortValue(a)
      const bv = col.sortValue(b)
      let cmp: number
      if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv
      else cmp = String(av).localeCompare(String(bv))
      return dir === 'asc' ? cmp : -cmp
    })
    return copy
  }, [rows, columns, sortKey, dir])

  const onSort = (key: string) => {
    if (key === sortKey) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      // Numeric columns feel natural high→low first; string columns A→Z.
      const col = columns.find((c) => c.key === key)
      setDir(col?.numeric ? 'desc' : 'asc')
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <TableScrollHint className="px-4 pt-3 !mb-1" />
      <div className="overflow-auto" style={{ maxHeight }}>
        <table
          className="w-full text-sm text-gray-900"
          // Inline rather than the `border-separate`/`border-spacing-0`
          // utilities: this is the repository's first use of either, Tailwind 4
          // emitted no rule for them here, and a frozen header silently loses
          // its dividers if the collapse mode is not the one assumed. An
          // inline declaration cannot fail to be generated.
          style={{ minWidth, borderCollapse: 'separate', borderSpacing: 0 }}
        >
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead>
            <tr>
              {columns.map((c, i) => {
                const active = c.key === sortKey
                const frozen = stickyFirstColumn && i === 0
                return (
                  <th
                    key={c.key}
                    className={`${frozen ? TH_STICKY_FIRST : TH} ${
                      c.numeric ? 'text-right' : 'text-left'
                    }`}
                    aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <button
                      type="button"
                      onClick={() => onSort(c.key)}
                      className={`inline-flex items-center gap-1 hover:text-gray-900 transition-colors ${
                        c.numeric ? 'flex-row-reverse' : ''
                      } ${active ? 'text-[#0469f0]' : ''}`}
                    >
                      {c.header}
                      {active ? (
                        dir === 'asc' ? (
                          <ArrowUp className="w-3 h-3" />
                        ) : (
                          <ArrowDown className="w-3 h-3" />
                        )
                      ) : (
                        <span className="w-3 h-3 opacity-30">
                          <ArrowDown className="w-3 h-3" />
                        </span>
                      )}
                    </button>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => {
              const isMuted = muted?.(row)
              return (
                <tr key={rowKey(row)} className={`${ROW} ${isMuted ? 'text-gray-500' : ''}`}>
                  {columns.map((c, i) => (
                    <td
                      key={c.key}
                      className={`${c.numeric ? TD_NUM : TD}${
                        stickyFirstColumn && i === 0 ? STICKY_CELL : ''
                      }`}
                    >
                      {c.render(row)}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
